import { useState, useMemo } from "react";
import {
  ALL_HOURS,
  getBrowserTimezone,
  getCurrentHourInTz,
  getLocalDateString,
  getTimezoneOptions,
  isSameDateInTz,
} from "@/utils/schedule.util";

interface UseScheduleFormOptions {
  initialTimezone?: string;
  initialStartDate?: Date;
  initialTimeOfDay?: string;
  initialEndDate?: Date | undefined;
  initialFrequency?: string;
  initialDayOfWeek?: string;
  initialDayOfMonth?: string;
}

export function useScheduleForm(options: UseScheduleFormOptions = {}) {
  const browserTimezone = getBrowserTimezone();
  const initTz = options.initialTimezone ?? browserTimezone;

  const defaultCurrentHour = getCurrentHourInTz(initTz);

  const defaultStartDate = (() => {
    if (options.initialStartDate) return options.initialStartDate;
    const today = new Date();
    if (defaultCurrentHour < 23) return today;
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  })();

  const defaultTimeOfDay = (() => {
    if (options.initialTimeOfDay) return options.initialTimeOfDay;
    if (defaultCurrentHour < 23) {
      const defaultHour = 6;
      const safeHour = defaultHour > defaultCurrentHour ? defaultHour : defaultCurrentHour + 1;
      return `${String(safeHour).padStart(2, "0")}:00`;
    }
    return "00:00";
  })();

  const [frequency, setFrequency] = useState(options.initialFrequency ?? "weekly");
  const [dayOfWeek, setDayOfWeek] = useState(options.initialDayOfWeek ?? "monday");
  const [dayOfMonth, setDayOfMonth] = useState(options.initialDayOfMonth ?? "1");
  const [timezone, setTimezone] = useState(initTz);
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [timeOfDay, setTimeOfDay] = useState(defaultTimeOfDay);
  const [endDate, setEndDate] = useState<Date | undefined>(options.initialEndDate);

  const timezoneOptions = useMemo(
    () => getTimezoneOptions(initTz),
    [initTz]
  );

  const isStartDateToday = isSameDateInTz(startDate, new Date(), timezone);
  const currentHourInTimezone = isStartDateToday ? getCurrentHourInTz(timezone) : -1;

  const availableHours = useMemo(() => {
    return ALL_HOURS.filter((t) => Number(t.split(":")[0]) > currentHourInTimezone);
  }, [currentHourInTimezone]);

  const isTodayDisabled = getCurrentHourInTz(timezone) >= 23;

  const isDateDisabled = (date: Date): boolean => {
    const todayStr = getLocalDateString(new Date(), timezone);
    const dateStr = getLocalDateString(date, timezone);
    if (dateStr < todayStr) return true;
    if (dateStr === todayStr && isTodayDisabled) return true;
    return false;
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    setStartDate(date);
    const newIsToday = isSameDateInTz(date, new Date(), timezone);
    if (newIsToday) {
      const newCurrentHour = getCurrentHourInTz(timezone);
      const selectedHour = Number(timeOfDay.split(":")[0]);
      if (selectedHour <= newCurrentHour) {
        const nextHour = newCurrentHour + 1;
        setTimeOfDay(nextHour <= 23 ? `${String(nextHour).padStart(2, "0")}:00` : "00:00");
      }
    }
  };

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz);
    try {
      const newCurrentHour = getCurrentHourInTz(tz);
      const newIsToday = isSameDateInTz(startDate, new Date(), tz);
      if (newIsToday) {
        const selectedHour = Number(timeOfDay.split(":")[0]);
        if (selectedHour <= newCurrentHour) {
          const nextHour = newCurrentHour + 1;
          setTimeOfDay(nextHour <= 23 ? `${String(nextHour).padStart(2, "0")}:00` : "00:00");
        }
      }
      const todayStr = getLocalDateString(new Date(), tz);
      const startStr = getLocalDateString(startDate, tz);
      if (startStr < todayStr || (startStr === todayStr && newCurrentHour >= 23)) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(tomorrow);
      }
    } catch (e) {
      void e;
    }
  };

  return {
    frequency,
    setFrequency,
    dayOfWeek,
    setDayOfWeek,
    dayOfMonth,
    setDayOfMonth,
    timezone,
    startDate,
    timeOfDay,
    setTimeOfDay,
    endDate,
    setEndDate,
    timezoneOptions,
    availableHours,
    isDateDisabled,
    handleStartDateChange,
    handleTimezoneChange,
  };
}
